import {
  UserPermission,
  UserPermissions,
  UserRole,
} from "../plugins/Cruxtify/UserPermission";
import {
  DateTimeField,
  EmailField,
  ImageField,
  LongTextField,
  NumberField,
  RelationshipField,
  RelationshipFieldMany,
  SelectField,
  StringField,
  UrlField,
} from "../plugins/Cruxtify/field-types";

import {
  UserRole as ErUserRole,
  ExpertStatus,
  getAsChoices,
  PartyType,
} from "../enums";
import {
  DateTimeProperty,
  NumberProperty,
  RelationshipProperty,
  RelationshipPropertyMany,
  StringProperty,
  UrlProperty,
} from "../plugins/Cruxtify/property-types";
import { ErCrudModel } from "./ErCrudModel";

export class Expert extends ErCrudModel {
  public static icon = "mdi-account-tie";
  protected static _typeLabel = "Expert";
  protected static asProperty = "expert";
  public static api = {
    path: "experts",
  };
  protected static routeBase = "experts";
  protected static userPermissions = new UserPermissions(
    {
      [UserRole.SuperAdmin]: UserPermission.Edit,
      [UserRole.EntityOwner]: UserPermission.Edit,
      [UserRole.LoggedIn]: UserPermission.New,
    },
    UserPermission.Read
  );

  protected userIdField = ["user"];

  protected static propertyDefinitions = [
    {
      type: NumberProperty,
      opts: {
        name: "id",
        label: "ID",
        userPermissions: UserPermission.Hidden,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "created_at",
        label: "Entry Created At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "updated_at",
        label: "Entry Updated At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: DateTimeProperty,
      opts: {
        name: "deleted_at",
        label: "Entry Deleted At",
        userPermissions: UserPermission.Read,
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "name",
        label: "Preferred Name",
        rules: ["required"],
        description:
          "What an attorney will see when you respond affirmatively to a case submission.\nEx: Susan Smith, MD",
      },
    },
    {
      type: "ImageProperty",
      opts: {
        name: "headshot",
        label: "Headshot",
        maxFileSize: 45 * 1024 * 1024, // 45 MB in bytes
        allowedFileTypes: ["image/jpeg", "image/png"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "preferred_party_type",
        label: "Preferred Party",
        // rules: ["required"],
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "status",
        label: "Profile Status",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "bio",
        label: "Introduction Message",
        // rules: ["required"],
        description:
          "This message accompanies your response when expressing interest in a case.\nEx: I am a board-certified emergency physician with over 15 years experience.  I've worked in large trauma centers and rural critical access hospitals.  I have over 5 years experience as an expert witness with an equal balance of plaintiff and defense work.",
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "email",
        label: "Email Address",
        computed: true,
      },
    },
    {
      type: StringProperty,
      opts: {
        name: "phone",
        label: "Phone Number",
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "user",
        label: "User",
        relatedModel: "User",
        computed: true,
      },
    },
    {
      type: RelationshipProperty,
      opts: {
        name: "profession",
        label: "Profession",
        relatedModel: "Profession",
        description: "Select your healthcare profession",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "sub_specialties",
        label: "Specialties",
        relatedModel: "SubSpecialty",
        description:
          "Choose up to five specialties.  If you don't see yours here, contact us and we will make the necessary changes or suggestions.",
        // rules: ["required"],
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "attachments",
        label: "Attachments",
        description:
          "Optional, but including attachments significantly increases your chance of being retained.  As always, this information is only shared with attorneys AFTER you express interest in a case.",
        relatedModel: "Attachment",
        foreignProperty: "expert",
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "expert_matches",
        label: "Expert Matches",
        relatedModel: "ExpertMatch",
        foreignProperty: "expert",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Hidden
        ),
      },
    },
    {
      type: RelationshipPropertyMany,
      opts: {
        name: "notifications",
        label: "Notifications",
        relatedModel: "Notification",
        foreignProperty: "expert",
        userPermissions: new UserPermissions(
          {
            [UserRole.SuperAdmin]: UserPermission.Edit,
          },
          UserPermission.Read
        ),
      },
    },
    {
      type: UrlProperty,
      opts: {
        name: "website",
        label: "Website or Profile URL",
        description:
          "Optional. A link to your website or professional profile (e.g., LinkedIn, etc.).",
      },
    },
  ];

  protected static fieldDefinitions = [
    { type: NumberField, opts: { property: "id" } },
    {
      type: DateTimeField,
      opts: { property: "created_at" },
    },
    { type: DateTimeField, opts: { property: "updated_at" } },
    { type: DateTimeField, opts: { property: "deleted_at" } },
    {
      type: StringField,
      opts: { property: "name", md: 6, isPrimaryLabelField: true },
    },
    {
      type: ImageField,
      opts: { property: "headshot", cols: 12 },
    },
    {
      type: EmailField,
      opts: { property: "email" },
    },
    {
      type: StringField,
      opts: { property: "phone", md: 6 },
    },
    {
      type: LongTextField,
      opts: { property: "bio", maxLength: 600 },
    },
    {
      type: SelectField,
      opts: {
        property: "status",
        cols: 4,
        options: getAsChoices(ExpertStatus),
      },
    },
    {
      type: SelectField,
      opts: {
        property: "preferred_party_type",
        cols: "auto",
        options: getAsChoices(PartyType),
        md: 4,
      },
    },

    {
      type: RelationshipField,
      opts: {
        property: "user",
      },
    },
    {
      type: RelationshipField,
      opts: {
        property: "profession",
        md: 6,
        orderBy: "order",
        orderDesc: false,
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "sub_specialties",
        maxItems: 5,
        md: 8,
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "expert_matches",
        label: "Matched Submissions",
        collectionLayoutOpts: {
          enableSearch: false,
          noRefresh: true,
          enableDynamicFilters: false,
        },
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "notifications",
        label: "Notifications",
        collectionLayoutOpts: {
          enableSearch: false,
          noRefresh: true,
          enableDynamicFilters: false,
        },
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: RelationshipFieldMany,
      opts: {
        property: "attachments",
        collectionLayoutOpts: {
          enableSearch: false,
          noRefresh: true,
          enableDynamicFilters: false,
          inlineEdit: true,
        },
        viewComponentName: "form-relationship-field-many-table",
        formComponentName: "form-relationship-field-many-table",
      },
    },
    {
      type: UrlField,
      opts: {
        property: "website",
        md: 12,
      },
    },
  ];

  // "expert-profile", not "expert": dd16eb7 renamed this very layout (and split
  // "expert-settings" off it) but left this default naming the old id, so every
  // expert-role visit to /experts/:id threw out of CrudModel.getModelLayout --
  // Sentry CLIENT-EXPERTRETAINER-COM-3 / -4.
  protected getDefaultModelLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin
      ? "admin"
      : this.$nuxt.$auth.user.user_role === ErUserRole.Expert
      ? "expert-profile"
      : "client";
  }

  protected static modelLayoutDefinitions = [
    {
      type: "TabbedModelLayout",
      opts: {
        id: "admin",
        tabs: [
          {
            id: "overviewTab",
            label: "Overview",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "Crudform",
                fieldSets: [
                  {
                    fields: ["status", "user.is_verified", "user"],
                  },
                  {
                    fields: [
                      "profession",
                      "preferred_party_type",
                      "sub_specialties",
                    ],
                  },
                  {
                    md: 4,
                    fields: ["headshot"],
                  },
                  {
                    md: 8,
                    fields: ["name", "phone", "bio", "website"],
                  },
                  {
                    fields: ["attachments"],
                  },
                  {
                    fields: ["created_at", "updated_at", "id"],
                  },
                ],
              },
            },
          },

          {
            id: "overviewTab",
            label: "Submission Matches",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "Crudform",
                fieldSets: [
                  {
                    id: "experts",
                    fields: ["expert_matches"],
                  },
                ],
              },
            },
          },

          {
            id: "overviewTab",
            label: "Notifications",
            content: {
              type: "FieldSetGridLayout",
              opts: {
                id: "Crudform",
                fieldSets: [
                  {
                    id: "notif",
                    fields: ["notifications"],
                  },
                ],
              },
            },
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "expert-profile",
        maxWidth: "900px",
        footerComponents: ["expert-publish-model-action"],
        disableDefaultModelActions: true,
        // navigateAfterSave: "/submissions", // this is handled in ExpertPublishModelAction.vue
        fieldSets: [
          {
            md: 4,
            fields: ["headshot"],
          },
          {
            md: 8,
            fields: ["name", "phone", "website", "bio"],
          },
          {
            fields: ["attachments"],
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "expert-settings",
        maxWidth: "900px",
        navigateAfterSave: "/submissions",
        fieldSets: [
          {
            fields: ["profession", "preferred_party_type", "sub_specialties"],
          },
        ],
      },
    },
    {
      type: "FieldSetGridLayout",
      opts: {
        id: "client",
        fieldSets: [
          {
            fields: [
              "name",
              "headshot",
              "phone",
              "email",
              "website",
              "bio",
              "attachments",
            ],
          },
        ],
      },
    },
  ];

  public static infoText = {
    matchingSettings:
      "This information drives our matching algorithm. Choose the specialties and party type that match your preferences and we will do the rest. It's that easy.",
    profile:
      "Ensure your profile is accurate and complete to increase your chances of matching with attorneys. Your information is sent to attorneys ONLY when you respond to a case submission and will NEVER be shared without approval. We DO NOT have a public directory and will always protect your anonymity and privacy.",
  };

  // No expert-specific collection layout has ever been declared here -- 933325c
  // routed experts at an "expert" id it never added, so /experts threw out of
  // CrudModel.getCollectionLayout for them. Experts get the same non-admin
  // listing clients do; add a branch back only alongside a layout to route to.
  protected static getDefaultCollectionLayoutId(): string {
    return this.$nuxt.$auth.user.is_admin ? "admin" : "client";
  }

  protected static collectionLayoutDefinitions = [
    {
      id: "admin",
      useModal: true,
      collectionActionComponents: ["collection-export"],
      columnFields: [
        "name",
        "status",
        "profession",
        "sub_specialties",
        "preferred_party_type",
        "website",
        "updated_at",
      ],
      enableSearch: true,
      enableDynamicFilters: true,
      dynamicFilterOptions: {
        filterFields: [
          "created_at",
          "name",
          "preferred_party_type",
          "status",
          "user.is_verified",
          "sub_specialties",
          "expert_matches",
          "updated_at",
          "user",
        ],
      },
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
    {
      id: "client",
      useModal: true,
      columnFields: ["name", "email", "website", "sub_specialties"],
      enableSearch: false,
      enableDynamicFilters: false,
      defaultSort: [
        {
          field: "created_at",
          order: "desc",
        },
      ],
    },
  ];

  public publish() {
    return this.$nuxt.$api.PUT(`experts/${this.id}/publish`);
  }
}
